import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Grid,
  useToast,
  Flex,
  Box,
} from "@chakra-ui/react";

import {useContext, useState, useEffect} from "react";
import {provinceOptions} from "../../utils/locationOptions";
import {INSURANCE_FIELDS, updatePatient} from "./helpers/updatePatient.js";
import UnlimitedInsurers, {policyEndsBeforeItStarts} from "./Fields/UnlimitedInsurers.jsx";

export default function Insurance({state, dispatch}) {
  const [patient, setPatient] = useState(state.patient);
  const [prevPatient, setPrevPatient] = useState(state.patient);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const [lastInputLength, setLastInputLength] = useState(0);
  const [isValid, setIsValid] = useState({
    healthCardVerCode: true,
    healthCardExpiry: true,
  });

  useEffect(() => {
    if (prevPatient !== state.patient) {
      setPrevPatient(state.patient);
    }
  }, [state.patient]);

  const validateField = (field, value) => {
    let regex = "";
    switch (field) {
      case "healthCardVerCode":
        regex = /^([A-Z]{1,2})?$/;
        break;
      case "healthCardExpiry":
        regex =
          /^(\d{0,4})(\/(0[1-9]?|1[0-2]?)?)?(\/(0[1-9]?|[12][0-9]?|3[01]?)?)?$/;
        break;
      default:
        regex = "";
    }
    return regex.test(value);
  };

  const hasMissingInsurerIdentifiers = (tppEntry) => {
    if (!patient?.pid || !tppEntry?.insurerId) {
      return false;
    }

    const claimEmployeeOrIdentificationNumber =
      tppEntry.memberId || tppEntry.insurerNo;
    const requiredFields = [
      claimEmployeeOrIdentificationNumber,
      tppEntry.policyNo,
    ];

    return requiredFields.some((value) => !String(value || "").trim());
  };
  
  return (
    <Box w="full" px={0} py={2}>
    <form
 
      onSubmit={async (e) => {
        try {
          setIsLoading(true);
          e.preventDefault();
       
          if (
            Array.isArray(patient?.tpp) &&
            patient.tpp.some(hasMissingInsurerIdentifiers)
          ) {
            toast({
              title: "Information Not Updated",
              description:
                "Policy, Group, or Plan Number and Claim, Employee, or Identification Number are required when the patient has an ID.",
              status: "error",
              isClosable: true,
            });
            return;
          }

          if (Array.isArray(patient?.tpp) && patient.tpp.some(policyEndsBeforeItStarts)) {
            toast({
              title: "Information Not Updated",
              description: "Active Until must be on or after Active Since.",
              status: "error",
              isClosable: true,
            });
            return;
          }

          if (Object.values(isValid).every((value) => value === true)) {
            await updatePatient({
              patientData: patient,
              fields: INSURANCE_FIELDS,
              state,
            });

            dispatch({
              type: "UPDATE_PATIENT",
              payload: patient,
            });
            toast({
              title: "Saved",
              description: "Profile updated successfully",
              status: "success",
              duration: 4000,
              isClosable: true,
            });
          } else {
            toast({
              title: "Information Not Updated",
              description: "Invalid Fields",
              status: "error",
              isClosable: true,
            });
          }
        } catch (error) {
          toast({
            title: "Information Not Updated",
            description: error?.response?.data || error.message,
            status: "error",
            isClosable: true,
          });
        } finally {
          setIsLoading(false);
        }
      }}
    
    >
      <VStack
        spacing={4}
        bg="gray.50"
        borderRadius="12px"
        px={{base: 4, md: 6}}
        py={{base: 4, md: 6}}
        mb={4}
      >
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={3}
          mb="3"
        >
          <FormControl id="healthCardNo">
            <FormLabel>Health Card Number</FormLabel>
            <Input
              size="sm"
              variant="outline"
              borderRadius="md"
              type="text"
              placeholder="999-999-999"
              value={patient.healthCard?.healthCardNo}
              onChange={(e) => {
                setPatient({
                  ...patient,
                  healthCard: {
                    ...patient?.healthCard,
                    healthCardNo: e.target.value,
                  },
                });
              }}
            />
          </FormControl>
          {(patient.healthCard?.healthCardState || patient.state) === "Ontario" && (
          <FormControl
            id="healthCardVerCode"
            isInvalid={!isValid.healthCardVerCode}
          >
            <FormLabel>Health Card Ver. Code</FormLabel>
            <Input
              size="sm"
              variant="outline"
              borderRadius="md"
              type="text"
              placeholder="YM"
              value={patient.healthCard?.healthCardVerCode}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                const valid = validateField("healthCardVerCode", value);
                setPatient({
                  ...patient,
                  healthCard: {
                    ...patient?.healthCard,
                    healthCardVerCode: valid
                      ? value
                      : patient.healthCard?.healthCardVerCode
                      ? patient.healthCard.healthCardVerCode
                      : "",
                  },
                });
              }}
              onBlur={(e) => {
                if (e.target.value && e.target.value.length < 2) {
                  setIsValid({
                    ...isValid,
                    healthCardVerCode: false,
                  });
                } else {
                  setIsValid({...isValid, healthCardVerCode: true});
                }
              }}
            />
          </FormControl>
          )}

          <FormControl
            id="healthCardExpiry"
            isInvalid={!isValid.healthCardExpiry}
          >
            <FormLabel>Health Card Expiry</FormLabel>
            <Input
              size="sm"
              variant="outline"
              borderRadius="md"
              type="text"
              placeholder="yyyy/mm/dd"
              value={patient.healthCard?.healthCardExpiry}
              onChange={(e) => {
                let value = e.target.value;
                if (value.length === 4 && value.length >= lastInputLength) {
                  value += "/";
                } else if (
                  value.length === 7 &&
                  value.length >= lastInputLength
                ) {
                  value += "/";
                }
                const valid = validateField("healthCardExpiry", value);
                setPatient({
                  ...patient,
                  healthCard: {
                    ...patient?.healthCard,
                    healthCardExpiry: valid
                      ? value
                      : patient.healthCard?.healthCardExpiry
                      ? patient.healthCard.healthCardExpiry
                      : "",
                  },
                });
                setLastInputLength(value.length);
              }}
              onBlur={(e) => {
                if (e.target.value && e.target.value.length < 10) {
                  setIsValid({
                    ...isValid,
                    healthCardExpiry: false,
                  });
                } else {
                  setIsValid({...isValid, healthCardExpiry: true});
                }
              }}
            />
          </FormControl>

          <FormControl id="healthCardState">
            <FormLabel>Province</FormLabel>
            <Select
              size="sm"
              variant="outline"
              borderRadius="md"
              placeholder="Select province"
              value={patient.healthCard?.healthCardState || patient.state || ""}
              onChange={(e) => {
                setPatient({
                  ...patient,
                  healthCard: {
                    ...patient?.healthCard,
                    healthCardState: e.target.value,
                  },
                });
              }}
            >
              {provinceOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <UnlimitedInsurers state={state} patient={patient} setPatient={setPatient} />
      </VStack>
      <Button
        type="submit"
        isLoading={isLoading}
        isDisabled={isLoading}
        variant="outline"
        borderColor="gray.300"
        bg="white"
        _hover={{bg: "#f8f9fa"}}
        my={3}
        px="8"
        size="sm"
        display="block"
        mx="auto"
      >
        Save
      </Button>
    </form>
    </Box>
  );
}
